import TileGrid from 'ol/tilegrid/TileGrid';
import { createXYZ } from 'ol/tilegrid';
import { TileWMS } from 'ol/source';

import TileLayer from 'ol/layer/Tile';
import { WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import { fetchTileWithRefreshControlAndCorsFallback } from '../Tiling/tileLoadFunction';
import {
  initLayerItem,
  RemoveLayerOnThresholdExceeded,
  useLayers,
} from '../layerManagement/LayerContext';
import { LayerItem } from '../layerManagement/types';
import {
  getProxyUrl,
  isExternalUrl,
  getUrlOrigin,
} from '../utils/corsProxy';

const getLayerOpacity = (wmsSettings: WMSInfo): number => {
  let opacity = 1;

  if (wmsSettings.trans !== undefined) {
    opacity = (100 - wmsSettings.trans) / 100;
  }

  if (wmsSettings.transparency !== undefined) {
    opacity = (100 - wmsSettings.transparency) / 100;
  }

  return opacity;
};

export interface LayerOptions {
  layerItem: LayerItem;
  removeLayerOnThresholdExceeded: RemoveLayerOnThresholdExceeded;
  onLoadFail: (layerItem: LayerItem) => void | undefined;
}

export const S57ToTileLayer = (url: string) => {
  const wmsLayerInfo: WMSLayerInfo = {
    id: 's57',
    title: 'ENC',
    name: 'ENC',
    coordinateSystems: ['EPSG:3857'],
    boundingBox: [
      {
        crs: 'EPSG:3857',
        extent: [
          -20037508.342789244, -20037508.342789244, 20037508.342789244,
          20037508.342789244,
        ],
      },
    ],
    enabled: true,
    isBase: true,
  };
  const wmsInfo: WMSInfo = {
    id: 's57',
    enabled: true,
    exception: '',
    name: 's57',
    patch: null,
    refreshrate: 300,
    selected: ['s57'],
    url: url,
    version: '1.3.0',
    baseMap: true,
    layers: [wmsLayerInfo],
  };

  return wmsToTileLayer(wmsInfo, wmsLayerInfo, undefined);
};

/**
 * Sanitizes a WMS URL by separating the base URL from allowed parameters
 * @param url The original WMS URL with all parameters
 * @param bannedParams Optional array of parameter names to remove
 * @returns An object containing the base URL and filtered parameters
 */
export const sanitizeWmsUrl = (
  url: string,
  bannedParams: string[] = [
    'SERVICE',
    'REQUEST',
    'VERSION',
    'LAYERS',
    'STYLES',
    'CRS',
    'SRS',
    'BBOX',
    'WIDTH',
    'HEIGHT',
    'FORMAT',
  ]
): { baseUrl: string; params: Record<string, string> } => {
  // If URL is empty or invalid, return defaults
  if (!url) {
    return { baseUrl: '', params: {} };
  }

  try {
    // Parse the URL
    const urlObj = new URL(url);

    // Get base URL without query parameters
    const baseUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;

    // Extract parameters that aren't banned
    const params: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      // Keep parameter if it's not in the banned list (case-insensitive check)
      if (
        !bannedParams.some(
          (banned) => banned.toLowerCase() === key.toLowerCase()
        )
      ) {
        params[key] = value;
      }
    });

    return { baseUrl, params };
  } catch (error) {
    console.error('Error parsing WMS URL:', error);
    return { baseUrl: url, params: {} }; // Return original URL if parsing fails
  }
};

export const wmsToTileLayer = (
  wmsInfo: WMSInfo,
  wmsLayerInfo: WMSLayerInfo,
  layerOptions?: LayerOptions
): TileLayer<any> => {
  let extent;
  const defaultExtent = [
    -20037508.342789244, -20037508.342789244, 20037508.342789244,
    20037508.342789244,
  ];

  // Check if boundingBox is available and has the correct CRS
  const boundingBox = wmsLayerInfo.boundingBox?.find(
    (box) => box.crs === 'EPSG:3857'
  );

  if (boundingBox && boundingBox.extent) {
    extent = boundingBox.extent;
  } else {
    extent = defaultExtent;
  }

  // Create a TileGrid for the WMS source
  const tileGrid = new TileGrid({
    extent: extent,
    resolutions: createXYZ().getResolutions(),
    tileSize: 256,
  });

  const { baseUrl, params: existingParams } = sanitizeWmsUrl(wmsInfo.url);

  // Only use proxy if explicitly set in persisted settings.
  // Do NOT use corsProxyRequiredOrigins cache here - it's only for runtime fallback
  // within a single layer, not for forcing proxy on all layers from the same origin.
  const needsProxy = wmsInfo.useProxy === true;
  const initialUrl = needsProxy ? getProxyUrl(baseUrl) : baseUrl;

  const wmsSource = new TileWMS({
    url: initialUrl,
    params: {
      LAYERS: wmsLayerInfo.name,
      CRS: 'EPSG:3857',
      TRANSPARENT: 'TRUE',
      FORMAT: 'image/png',
      ...existingParams,
    },
    cacheSize: 2048,
    projection: 'EPSG:3857',
    tileGrid: tileGrid,
    crossOrigin: 'anonymous',
  });

  // Set tileLoadFunction after creating the source so we can pass the source reference
  wmsSource.setTileLoadFunction(
    fetchTileWithRefreshControlAndCorsFallback(
      wmsInfo.refreshrate || 0,
      undefined,
      5,
      layerOptions,
      wmsSource
    )
  );

  return new TileLayer({
    opacity: getLayerOpacity(wmsInfo),
    source: wmsSource,
  });
};

const isWMSValid = async (wmsInfo: WMSInfo) => {
  try {
    // Parse the URL to preserve existing query parameters
    const url = new URL(wmsInfo.url);

    // Strip WMS-controlled params (case-insensitive) to avoid duplicates
    const paramsToStrip = ['service', 'request', 'version'];
    for (const param of [...url.searchParams.keys()]) {
      if (paramsToStrip.includes(param.toLowerCase())) {
        url.searchParams.delete(param);
      }
    }

    // Add WMS parameters
    url.searchParams.set('SERVICE', 'WMS');
    url.searchParams.set('VERSION', '1.1.1');
    url.searchParams.set('REQUEST', 'GetCapabilities');

    const fullUrl = url.toString();
    const origin = getUrlOrigin(fullUrl);
    const external = isExternalUrl(fullUrl);

    // Only use proxy if explicitly set in persisted settings.
    // Do NOT use corsProxyRequiredOrigins cache here - it should only affect
    // runtime tile loading fallback, not validation of other layers.
    if (external && wmsInfo.useProxy === true) {
      const proxyUrl = getProxyUrl(fullUrl);
      const response = await fetch(proxyUrl);
      return response.ok;
    }

    // Try direct request first for external URLs
    if (external) {
      try {
        const response = await fetch(fullUrl);
        if (response.ok) {
          return true;
        }
        // Non-OK response (4xx, 5xx) - don't proxy, just return false
        return false;
      } catch (error) {
        // CORS errors manifest as TypeError with "Failed to fetch" message
        const isCorsLikeError = error instanceof TypeError || 
          (error instanceof Error && error.message.includes('Failed to fetch'));
        
        if (isCorsLikeError) {
          // Try proxy fallback for this validation, but do NOT add to shared cache.
          // The cache would cause all other layers from this origin to use proxy,
          // even if they don't have CORS issues.
          const proxyUrl = getProxyUrl(fullUrl);
          const response = await fetch(proxyUrl);
          return response.ok;
        }
        // Other errors - don't proxy
        return false;
      }
    }

    // Internal URL - fetch directly
    const response = await fetch(fullUrl);
    if (!response.ok) {
      console.warn(
        `Failed to validate WMS: ${wmsInfo.url} (${response.status})`
      );
      return false;
    }
    return true;
  } catch (error) {
    console.error('WMS validation error:', error);
    return false;
  }
};

export const useWmsToTileLayer = (
  onLoadFail: (layerItem: LayerItem) => void | undefined
) => {
  const { removeLayerOnThresholdExceeded } = useLayers();

  const onWmsToTileLayerWithLayerItem = async (
    wmsInfo: WMSInfo,
    wmsLayerInfo: WMSLayerInfo
  ) => {
    if (!(await isWMSValid(wmsInfo))) {
      return { tileLayer: undefined, layerItem: undefined };
    }

    const emptyLayer = new TileLayer({
      opacity: 1.0,
      source: undefined,
    });
    const layerItem = initLayerItem(
      emptyLayer,
      wmsLayerInfo.title,
      false,
      true,
      false,
      false,
      { type: 'wms', wmsLayerId: wmsLayerInfo.id },
      wmsLayerInfo.id
    );

    const layerOptions: LayerOptions = {
      layerItem,
      removeLayerOnThresholdExceeded,
      onLoadFail,
    };

    // Create the WMS tile layer
    const tileLayer = wmsToTileLayer(wmsInfo, wmsLayerInfo, layerOptions);

    return { tileLayer, layerItem };
  };

  return onWmsToTileLayerWithLayerItem;
};
